// src/components/QuotaDashboard.js
// Marker: quota_v1
// Dark-theme dashboard widget showing LINE quota summary

import React, { useState, useEffect, useCallback } from 'react';
import API from '../api';
import { connectSSE } from '../lib/sse';
import { Activity, AlertTriangle, RefreshCw } from 'lucide-react';

const STATUS_INFO = {
  warning:  { color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.15)', icon: '⚠️', label: 'ใกล้หมด' },
  critical: { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.15)', icon: '🔴', label: 'วิกฤต' },
  exceeded: { color: '#b91c1c', bg: 'rgba(185, 28, 28, 0.20)', icon: '❌', label: 'เกินโควต้า' },
};

export default function QuotaDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await API.get('/api/quota/dashboard');
      setData(res.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    // SSE: refresh when alert fires
    const conn = connectSSE({
      onQuotaAlert: () => { load(); }
    });
    return () => { if (conn && conn.close) conn.close(); };
  }, [load]);

  if (loading) {
    return (
      <div style={{ background: '#1e293b', borderRadius: 12, padding: 16, marginBottom: 16, color: '#94a3b8', fontSize: 14 }}>
        กำลังโหลดข้อมูล quota...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ background: '#1e293b', borderRadius: 12, padding: 16, marginBottom: 16, color: '#fca5a5', fontSize: 14 }}>
        ⚠️ {error}
      </div>
    );
  }

  if (!data || data.total_channels === 0) {
    return null;
  }

  const hasAlerts = data.alerts && data.alerts.length > 0;
  const totalPercent = data.total_limit > 0
    ? ((data.total_used / data.total_limit) * 100).toFixed(1)
    : 0;

  return (
    <div style={{
      background: '#1e293b',
      borderRadius: 12,
      padding: 20,
      marginBottom: 16,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ background: '#38bdf820', borderRadius: 8, padding: 8 }}>
            <Activity size={18} color="#38bdf8" />
          </div>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#f1f5f9' }}>
            LINE Quota Status
          </h3>
        </div>
        <button
          onClick={load}
          title="Refresh"
          style={{
            background: '#0f172a',
            border: '1px solid #334155',
            borderRadius: 6,
            padding: '5px 10px',
            cursor: 'pointer',
            color: '#94a3b8',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 12,
          }}
        >
          <RefreshCw size={12} />
          Refresh
        </button>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
        gap: 10,
        marginBottom: hasAlerts ? 16 : 0,
      }}>
        <StatCard label="Channels" value={data.total_channels} color="#38bdf8" />
        <StatCard label="ปกติ" value={data.ok || 0} color="#22c55e" />
        <StatCard label="ใกล้หมด" value={data.warning || 0} color="#f59e0b" />
        <StatCard label="วิกฤต / เกิน" value={(data.critical || 0) + (data.exceeded || 0)} color="#ef4444" />
        {data.error > 0 && (
          <StatCard label="Error" value={data.error} color="#94a3b8" />
        )}
        {data.total_limit > 0 && (
          <StatCard
            label="ใช้รวม"
            value={`${totalPercent}%`}
            sub={`${data.total_used.toLocaleString()}/${data.total_limit.toLocaleString()}`}
            color="#a855f7"
          />
        )}
      </div>

      {hasAlerts && (
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: '#f1f5f9', display: 'flex', alignItems: 'center', gap: 6 }}>
            <AlertTriangle size={14} color="#f59e0b" />
            ต้องการความสนใจ ({data.alerts.length})
          </div>
          {data.alerts.map(a => {
            const info = STATUS_INFO[a.status] || STATUS_INFO.warning;
            const pct = a.limit > 0 ? ((a.used / a.limit) * 100).toFixed(1) : 0;
            return (
              <div key={a.id} style={{
                background: info.bg,
                borderLeft: `3px solid ${info.color}`,
                padding: '10px 14px',
                marginBottom: 6,
                borderRadius: 6,
                fontSize: 13,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#f1f5f9', fontWeight: 600 }}>
                    {info.icon} {a.name}
                  </span>
                  <span style={{ color: info.color, fontWeight: 700 }}>
                    {info.label} ({pct}%)
                  </span>
                </div>
                <div style={{ fontSize: 12, color: '#cbd5e1', marginTop: 4 }}>
                  ใช้ {a.used?.toLocaleString()} / {a.limit?.toLocaleString()} ข้อความ
                  {a.status === 'exceeded' && <span style={{ color: info.color }}> — บอทอาจตอบไม่ออก! ให้ลูกค้า upgrade plan</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, sub, color }) {
  return (
    <div style={{
      background: '#0f172a',
      border: `1px solid ${color}30`,
      borderRadius: 8,
      padding: '10px 14px',
      textAlign: 'center',
    }}>
      <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}
